import React from 'react';
import { Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const MMAPredictionControlBar = ({
  selectionCount,
  usageState,
  isLoading,
  error,
  refundNotice = false,
  onPredict,
  onClear,
  selectedFights = [],
  isMobile = false
}) => {
  if (selectionCount === 0 && !error && !refundNotice) return null;

  const totalCost = selectionCount * 7;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-black/95 border-t border-white/10 py-4 px-6"
      >
        {/* Desktop */}
        {!isMobile && (
          <>
            {error && (
              <div className="max-w-7xl mx-auto mb-3 p-3 bg-red-500/10 rounded-sm border border-red-500/30">
                <p className="text-xs text-red-200">{error}</p>
              </div>
            )}

            {refundNotice && (
              <div className="max-w-7xl mx-auto mb-3 p-3 bg-yellow-500/10 rounded-sm border border-yellow-500/30">
                <p className="text-xs text-yellow-200 font-semibold">💰 {refundNotice}</p>
              </div>
            )}

            <div className="max-w-7xl mx-auto flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-sm font-bold text-white">
                    {selectionCount} {selectionCount === 1 ? 'Fight Selected' : 'Fights Selected'}
                  </p>
                  <p className="text-xs text-white/50">
                    7 coins/fight • {totalCost} coins total
                  </p>
                </div>
              </div>

              {selectedFights?.length > 0 && (
                <div className="hidden md:flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">Picks:</span>
                  {selectedFights.slice(0, 4).map((fight, index) => {
                    const first = fight?.fighters?.first || fight?.fighters?.home || fight?.fighters?.red || fight?.fighters?.[0];
                    const second = fight?.fighters?.second || fight?.fighters?.away || fight?.fighters?.blue || fight?.fighters?.[1];
                    const firstName = first?.name?.split(' ').pop()?.toUpperCase() || 'F1';
                    const secondName = second?.name?.split(' ').pop()?.toUpperCase() || 'F2';
                    return (
                      <span key={fight.id || index} className="text-[10px] font-mono px-2 py-0.5 border border-white/10 rounded-sm text-white/70">
                        {firstName}v{secondName}
                      </span>
                    );
                  })}
                  {selectedFights.length > 4 && (
                    <span className="text-[10px] font-mono px-2 py-0.5 border border-white/10 rounded-sm text-white/70">
                      +{selectedFights.length - 4}
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={onClear}
                  className="px-4 py-2 text-sm font-semibold text-white/60 hover:text-white border border-white/10 rounded-sm"
                >
                  Clear
                </button>
                <button
                  onClick={onPredict}
                  disabled={!usageState.canUse || isLoading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-sm text-sm font-semibold transition-colors border ${
                    usageState.canUse && !isLoading
                      ? 'border-white/20 text-white hover:border-white/60'
                      : 'border-white/5 text-white/30 cursor-not-allowed'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      Predict Winners
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Mobile fallback */}
        {/* Error Message */}
        {error && (
          <div className="mb-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
            <p className="text-xs text-red-300">{error}</p>
          </div>
        )}

        {/* Refund Notice */}
        {refundNotice && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-3 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20"
          >
            <p className="text-xs text-yellow-300 font-medium">
              💰 {refundNotice}
            </p>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default MMAPredictionControlBar;
